(() => {
  const FALLBACK = 'International shipping calculated at checkout';
  const ROOT = window.Shopify?.routes?.root || '/';

  // Desk Nest Cat Bed variants
  const DESK_NEST_VARIANTS = {
    '45528060035264': 'deskSingle',
    '46696294711488': 'deskSingle',
    '46696294744256': 'deskDouble',
    '46696294777024': 'deskDouble'
  };

  // Lounge Nest product handles
  const LOUNGE_PRODUCTS = {
    'the-end-table': 'endTable',
    'the-double-decker-lounge': 'loungeDouble',
    'the-single-lounge-nest-bed': 'loungeSingle'
  };

  // Confirmed accessory product names.
  // Add alternative storefront titles here if necessary.
  const ACCESSORY_NAMES = [
    'wrap around pillow',
    'extra cushion covers',
    'clamp extension',
    'blanket',
    'groovy glove'
  ];

  // Rates in USD, per furniture unit.
  const RATES = {
    deskSingle: {
      US: 0,
      CA: 19,
      international: 49
    },
    deskDouble: {
      US: 0,
      CA: 29,
      international: 69
    },
    endTable: {
      US: 0,
      CA: 49,
      international: 119
    },
    loungeDouble: {
      US: 0,
      CA: 69,
      international: 149
    },
    loungeSingle: {
      US: 0,
      CA: 29,
      international: 49
    }
  };

  // Accessories-only order rules.
  const ACCESSORY_SHIPPING_USD = 9.99;
  const ACCESSORY_FREE_THRESHOLD_USD = 150;

  const BED_GROUPS = new Set([
    'deskSingle',
    'deskDouble',
    'loungeDouble',
    'loungeSingle'
  ]);

  let refreshTimer;
  let requestId = 0;

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function identifyGroup(item) {
    const variantId = String(item.variant_id || item.id);

    if (DESK_NEST_VARIANTS[variantId]) {
      return DESK_NEST_VARIANTS[variantId];
    }

    const handle = String(item.handle || '')
      .split('?')[0]
      .replace(/^.*\/products\//, '');

    if (LOUNGE_PRODUCTS[handle]) {
      return LOUNGE_PRODUCTS[handle];
    }

    const title = normalize(item.product_title || item.title);

    if (
      ACCESSORY_NAMES.some((name) =>
        title.includes(normalize(name))
      )
    ) {
      return 'accessory';
    }

    // Do not invent a shipping rate for an unrecognized item.
    return null;
  }

  function getRegion(country) {
    if (country === 'US') return 'US';
    if (country === 'CA') return 'CA';
    return 'international';
  }

  function calculateShipping(cart, country) {
    const region = getRegion(country);

    let shipping = 0;
    let accessoriesSubtotal = 0;
    let hasAccessories = false;
    let hasBed = false;

    for (const item of cart.items) {
      if (item.requires_shipping === false) {
        continue;
      }

      const group = identifyGroup(item);

      if (!group) {
        console.warn(
          'Unrecognized shipping product:',
          item.product_title,
          item.variant_id,
          item.handle
        );

        return null;
      }

      if (group === 'accessory') {
        hasAccessories = true;

        // Shopify cart prices are in currency subunits.
        accessoriesSubtotal += item.final_line_price / 100;
        continue;
      }

      const unitRate = RATES[group]?.[region];

      if (unitRate === undefined) {
        return null;
      }

      shipping += unitRate * item.quantity;

      if (BED_GROUPS.has(group)) {
        hasBed = true;
      }
    }

    if (hasAccessories && !hasBed && region !== 'US') {
      if (
        accessoriesSubtotal < ACCESSORY_FREE_THRESHOLD_USD
      ) {
        shipping += ACCESSORY_SHIPPING_USD;
      }
    }

    return shipping;
  }

  function formatUSD(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  function setMessage(message) {
    document
      .querySelectorAll('[data-shipping-estimate-message]')
      .forEach((element) => {
        element.textContent = message;
      });
  }

  async function refreshEstimate() {
    const container = document.querySelector(
      '[data-cart-shipping-estimate]'
    );

    if (!container) return;

    const country = container.dataset.country;

    // Keep the existing US message.
    if (!country || country === 'US') return;

    const currentRequest = ++requestId;

    try {
      const response = await fetch(`${ROOT}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();

      if (currentRequest !== requestId) return;

      if (!cart.item_count) {
        setMessage(FALLBACK);
        return;
      }

      const amount = calculateShipping(cart, country);

      if (amount === null) {
        setMessage(FALLBACK);
        return;
      }

      setMessage(
        amount === 0
          ? 'Estimated shipping: Free'
          : `Estimated shipping at checkout: ${formatUSD(amount)}`
      );

    } catch (error) {
      if (currentRequest !== requestId) return;

      console.warn('Shipping estimate:', error);
      setMessage(FALLBACK);
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);

    refreshTimer = setTimeout(refreshEstimate, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleRefresh
    );
  } else {
    scheduleRefresh();
  }

  document.addEventListener(
    'shopify:section:load',
    scheduleRefresh
  );

  window.addEventListener('pageshow', scheduleRefresh);

  // Detect cart page and cart drawer content updates.
  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => {
      const target = mutation.target;

      if (!(target instanceof Element)) return false;

      if (target.closest('[data-cart-shipping-estimate]')) {
        return false;
      }

      if (!target.closest('#drawer-cart, .template-cart')) {
        return false;
      }

      return [...mutation.addedNodes, ...mutation.removedNodes]
        .some((node) => {
          if (!(node instanceof Element)) return false;

          const selector =
            'cart-items, .cart-item, #cart-footer, ' +
            '[data-cart-shipping-estimate]';

          return node.matches(selector) ||
            Boolean(node.querySelector(selector));
        });
    });

    if (cartChanged) {
      scheduleRefresh();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();